import { z } from "zod";
import type { ChannelType } from "@ghostwatch/shared-types";
import {
  assetFilterSchema,
  channelTypeSchema,
  checkTypeSchema,
  maintenanceWindowPolicySchema,
  severitySchema,
  tagged,
  uuidSchema,
} from "./common";

// ---------------------------------------------------------------------------
// AlertCondition — externally tagged union, same pattern as CheckConfig.
// ---------------------------------------------------------------------------
export const checkFailedFields = z.object({ check_type: checkTypeSchema.optional() });
export const checkRecoveredFields = z.object({ check_type: checkTypeSchema.optional() });
export const certificateExpiringFields = z.object({ within_days: z.coerce.number().int().min(0).default(14) });
export const emptyConditionFields = z.object({});
export const agentMissedHeartbeatFields = z.object({ for_seconds: z.coerce.number().int().min(0).default(300) });
export const diskUsageAboveFields = z.object({ pct: z.coerce.number().min(0).max(100).default(90) });
export const cpuAboveFields = z.object({
  pct: z.coerce.number().min(0).max(100).default(90),
  for_seconds: z.coerce.number().int().min(0).default(60),
});
export const memoryAboveFields = z.object({
  pct: z.coerce.number().min(0).max(100).default(90),
  for_seconds: z.coerce.number().int().min(0).default(60),
});
export const sloBurnRateFields = z.object({
  slo_id: uuidSchema,
  factor: z.coerce.number().min(0).default(14.4),
});

export const alertConditionFieldSchemas = {
  check_failed: checkFailedFields,
  check_recovered: checkRecoveredFields,
  certificate_expiring: certificateExpiringFields,
  dns_changed: emptyConditionFields,
  agent_missed_heartbeat: agentMissedHeartbeatFields,
  disk_usage_above: diskUsageAboveFields,
  cpu_above: cpuAboveFields,
  memory_above: memoryAboveFields,
  service_down: emptyConditionFields,
  file_integrity_changed: emptyConditionFields,
  config_drift_detected: emptyConditionFields,
  ghostwire_unhealthy: emptyConditionFields,
  slo_burn_rate: sloBurnRateFields,
} as const;
export type AlertConditionKind = keyof typeof alertConditionFieldSchemas;
export const ALERT_CONDITION_KINDS = Object.keys(alertConditionFieldSchemas) as AlertConditionKind[];

export function buildAlertCondition<K extends AlertConditionKind>(
  kind: K,
  fields: z.infer<(typeof alertConditionFieldSchemas)[K]>,
) {
  return { [kind]: fields } as Record<K, typeof fields>;
}

export const alertConditionSchema = z.union([
  tagged("check_failed", checkFailedFields),
  tagged("check_recovered", checkRecoveredFields),
  tagged("certificate_expiring", certificateExpiringFields),
  tagged("dns_changed", emptyConditionFields),
  tagged("agent_missed_heartbeat", agentMissedHeartbeatFields),
  tagged("disk_usage_above", diskUsageAboveFields),
  tagged("cpu_above", cpuAboveFields),
  tagged("memory_above", memoryAboveFields),
  tagged("service_down", emptyConditionFields),
  tagged("file_integrity_changed", emptyConditionFields),
  tagged("config_drift_detected", emptyConditionFields),
  tagged("ghostwire_unhealthy", emptyConditionFields),
  tagged("slo_burn_rate", sloBurnRateFields),
]);

/** `POST /v1/alerts/rules` body. Mirrors `CreateAlertRule` in routes_ops.rs. */
export const createAlertRuleSchema = z.object({
  name: z.string().trim().min(1, "name is required").max(200),
  condition: alertConditionSchema,
  severity: severitySchema,
  dedupe_key_template: z.string().trim().optional(),
  for_duration_seconds: z.coerce.number().int().min(0).max(86_400).optional(),
  route_id: uuidSchema.optional().or(z.literal("")),
  maintenance_window_policy: maintenanceWindowPolicySchema.optional(),
  asset_filter: assetFilterSchema.optional(),
});
export type CreateAlertRuleInput = z.infer<typeof createAlertRuleSchema>;

export const updateAlertRuleSchema = z.object({
  enabled: z.boolean().optional(),
  severity: severitySchema.optional(),
  route_id: uuidSchema.optional(),
});
export type UpdateAlertRuleInput = z.infer<typeof updateAlertRuleSchema>;

// ---------------------------------------------------------------------------
// RouteDestination — determined 1:1 by `channel_type`.
// ---------------------------------------------------------------------------
export const emailDestinationFields = z.object({
  to: z.array(z.string().trim().email()).min(1, "at least one recipient is required"),
});
export const webhookDestinationFields = z.object({
  url: z.string().trim().url("must be a valid URL"),
  secret_ref: z.string().trim().optional().or(z.literal("")),
});
export const ghostwireMessengerDestinationFields = z.object({
  relay_url: z.string().trim().url("must be a valid URL"),
  queue_token_ref: z.string().trim().min(1, "queue token reference is required"),
});
export const slackWebhookDestinationFields = z.object({
  url: z.string().trim().url("must be a valid URL"),
});
export const pagerdutyDestinationFields = z.object({
  routing_key_ref: z.string().trim().min(1, "routing key reference is required"),
});
export const localDestinationFields = z.object({});

export const routeDestinationFieldSchemas = {
  email: emailDestinationFields,
  webhook: webhookDestinationFields,
  ghostwire_messenger: ghostwireMessengerDestinationFields,
  slack_webhook: slackWebhookDestinationFields,
  pagerduty: pagerdutyDestinationFields,
  local: localDestinationFields,
} as const satisfies Record<ChannelType, z.ZodTypeAny>;

export function buildRouteDestination<K extends ChannelType>(
  channelType: K,
  fields: z.infer<(typeof routeDestinationFieldSchemas)[K]>,
) {
  return { [channelType]: fields } as Record<K, typeof fields>;
}

export const routeDestinationSchema = z.union([
  tagged("email", emailDestinationFields),
  tagged("webhook", webhookDestinationFields),
  tagged("ghostwire_messenger", ghostwireMessengerDestinationFields),
  tagged("slack_webhook", slackWebhookDestinationFields),
  tagged("pagerduty", pagerdutyDestinationFields),
  tagged("local", localDestinationFields),
]);

/** `POST /v1/routes` body. Mirrors `CreateRoute` in routes_ops.rs. */
export const createRouteSchema = z.object({
  name: z.string().trim().min(1, "name is required").max(200),
  channel_type: channelTypeSchema,
  destination: routeDestinationSchema,
  severity_filter: z.array(severitySchema).default([]),
  include_payload: z.boolean().default(false),
  schedule_id: uuidSchema.optional().or(z.literal("")),
  escalation_policy_id: uuidSchema.optional().or(z.literal("")),
});
export type CreateRouteInput = z.infer<typeof createRouteSchema>;
